import { Tenancy_Rich_Asset_Type_Enum_Ps_Enum } from '@pypestream/api-services';

// The "Disable Free Text" behavior value. When present, free text input is
// closed (the user can only pick an option), so NLU never runs.
const DISABLE_FREE_TEXT = 'disable_input';

export interface AssetTypeAutoAdjust {
  /** Only present when the behaviors array needs to change. */
  behaviors?: string[];
  /** Only present when the NLU Disabled flag needs to change. */
  nluDisabled?: boolean;
}

/**
 * PSTUDIO-3422: when a node's rich asset type changes, automatically adjust
 * "Disable Free Text" (a `behaviors` value) and "NLU Disabled" to match the
 * selected asset type instead of leaving them alone and floating a validation
 * error. Returns only the fields that should change so the caller can apply
 * them in a single row update; an empty object means "leave everything as-is".
 *
 *  - File Upload : force-disable Free Text (add `disable_input`) and
 *                  force-disable NLU (`nluDisabled=true`, required by the PIF
 *                  compiler).
 *  - Date Picker : force-disable Free Text. NLU is then not applicable, so it
 *                  resolves to `false` and the existing rule greys the checkbox.
 *  - Secure Input: force-ENABLE Free Text (remove `disable_input`) and
 *                  force-disable NLU (`nluDisabled=true` — NLU isn't supported
 *                  at secure input nodes).
 *  - Anything else: behaviors are left untouched (no auto-revert). NLU still
 *                  resolves to `false` when free text remains closed, matching
 *                  the prior switch-away-from-file-upload behavior.
 */
export function getAssetTypeAutoAdjust(
  newType: string,
  currentBehaviors: readonly string[] | null | undefined
): AssetTypeAutoAdjust {
  const behaviors = Array.isArray(currentBehaviors)
    ? [...currentBehaviors]
    : [];
  const hasDisableFreeText = behaviors.includes(DISABLE_FREE_TEXT);

  const isFileUpload =
    newType === Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload;
  const isDatepicker =
    newType === Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Datepicker;
  const isSecure = newType === Tenancy_Rich_Asset_Type_Enum_Ps_Enum.Secure;

  let nextBehaviors: string[] | undefined;
  if ((isFileUpload || isDatepicker) && !hasDisableFreeText) {
    nextBehaviors = [...behaviors, DISABLE_FREE_TEXT];
  } else if (isSecure && hasDisableFreeText) {
    nextBehaviors = behaviors.filter((b) => b !== DISABLE_FREE_TEXT);
  }

  const freeTextClosed = (nextBehaviors ?? behaviors).includes(
    DISABLE_FREE_TEXT
  );

  const result: AssetTypeAutoAdjust = {};
  if (nextBehaviors) {
    result.behaviors = nextBehaviors;
  }
  if (freeTextClosed) {
    // NLU never runs with free text closed: file upload requires it checked
    // (compiler), every other type reads false.
    result.nluDisabled = isFileUpload;
  } else if (isSecure) {
    // Free text open but NLU still forced off for secure input nodes.
    result.nluDisabled = true;
  }
  return result;
}
